import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Empty, TabHeading, inputCls, labelCls, fmtDate } from "./shared";

const STATUS = ["present", "absent", "late", "excused"];
const badge = { present: "border-white bg-white/10", absent: "border-red-400/40 text-red-300", late: "border-white/25 text-white/60", excused: "border-white/25 text-white/60" };

export default function AttendanceTab({ athleteId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), status: "present", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance?athlete_id=${athleteId}`);
      setItems(data);
    } catch { toast.error("Failed to load attendance."); } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/attendance", { athlete_id: athleteId, ...form });
      toast.success("Attendance logged.");
      setForm({ date: new Date().toISOString().slice(0, 10), status: "present", notes: "" }); setShowAdd(false); load();
    } catch { toast.error("Failed to log attendance."); } finally { setSaving(false); }
  };

  const present = items.filter((a) => a.status === "present").length;

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="af-attendance">
      <TabHeading action={
        <button data-testid="af-attendance-add" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-white/85 transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "Log Attendance"}
        </button>
      }>Attendance</TabHeading>

      {showAdd && (
        <form onSubmit={save} className="bg-[#121212] border border-white/10 p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="af-attendance-form">
          <div><label className={labelCls}>Date</label><input data-testid="af-att-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Status</label>
            <select data-testid="af-att-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Notes</label><input data-testid="af-att-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
          <div className="sm:col-span-3"><button data-testid="af-att-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save</button></div>
        </form>
      )}

      {items.length === 0 ? (
        <Empty testid="af-attendance-empty">No attendance records yet.</Empty>
      ) : (
        <>
          <p className="font-mono-accent text-xs tracking-widest text-white/50 mb-4" data-testid="af-attendance-count">{present} PRESENT · {items.length} TOTAL SESSIONS</p>
          <div className="space-y-2" data-testid="af-attendance-list">
            {items.map((a) => (
              <div key={a.id} data-testid={`af-attendance-${a.id}`} className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-bold">{fmtDate(a.date)}</p>
                  {a.notes && <p className="text-white/40 text-xs mt-0.5">{a.notes}</p>}
                </div>
                <span className={`inline-flex text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border ${badge[a.status] || "border-white/25"}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
